import { useState, useEffect } from "react";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
    Card,
    CardMedia,
    CardContent,
    Chip,
} from "@mui/material";

//Custom Components
import ActionsMenu from "../../components/ActionsMenu";
import GalleryModal from "./components/GalleryModal";
import UploadModal from "./components/UploadModal";
// Icons
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import api from "../../utils/api";


export default function Gallery() {
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);

        try {
            const response = await api.get("admin/gallery", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });

            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });

            setRows(response.data.data);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (rows) => {
        try {
            // Delete multiple gallery items
            for (const rowId of rows) {
                await api.delete(`admin/gallery/${rowId}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.log(err)
        }
    };

    const handleOpenEditModal = (row) => {
        setRow(row);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = (refetch) => {
        setRow(null);
        setIsEditModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handleOpenUploadModal = () => {
        setIsUploadModalOpen(true);
    };

    const handleCloseUploadModal = (refetch) => {
        setIsUploadModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { alt: search };
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Gallery
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}>
                    <Box>
                        <FormControl sx={{ width: { xs: '100%', md: '45ch' } }} variant="outlined">
                            <OutlinedInput
                                size="small"
                                id="search"
                                placeholder="Search by alt text..."
                                sx={{ flexGrow: 1, background: "#fff" }}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ color: 'text.primary' }}>
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                inputProps={{
                                    'aria-label': 'search',
                                }}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Button variant="contained" size="small" startIcon={<AddIcon color='success' />} onClick={handleOpenUploadModal}>
                            Upload Images
                        </Button>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <Typography>Loading...</Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <Typography color="text.secondary">No images found</Typography>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 1,
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))',
                                    lg: 'repeat(4, minmax(0, 1fr))',
                                    xl: 'repeat(5, minmax(0, 1fr))',
                                },
                            }}
                        >
                            {rows.map((item) => {
                                const hasAlt = !!(item.alt_en || item.alt_hy || item.alt_ru);
                                const imgAlt = item.alt_en || item.alt_hy || item.alt_ru || 'Gallery image';
                                return (
                                    <Card
                                        key={item.id}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                aspectRatio: '1 / 1',
                                                flexShrink: 0,
                                                overflow: 'hidden',
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={item.url}
                                                alt={imgAlt}
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => handleOpenEditModal(item)}
                                            />
                                        </Box>
                                        <CardContent sx={{ pb: 1, pt: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, alignItems: 'center', minWidth: 0 }}>
                                                <Chip
                                                    label={hasAlt ? 'Alt Exists' : 'No alt'}
                                                    size="small"
                                                    color={hasAlt ? 'success' : 'error'}
                                                    onClick={() => handleOpenEditModal(item)}
                                                    sx={{ cursor: 'pointer' }}
                                                />
                                                {item.order !== null && item.order !== undefined && (
                                                    <Chip
                                                        label={`Order: ${item.order}`}
                                                        size="small"
                                                        color="default"
                                                    />
                                                )}
                                                <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                                                    <ActionsMenu
                                                        actions={[
                                                            { label: "Edit", onClick: () => handleOpenEditModal(item) },
                                                            {
                                                                label: "Delete",
                                                                onClick: () => handleDeleteRows([item.id]),
                                                                confirm: true,
                                                                confirmText: `Are you sure you want to delete this image?`,
                                                            },
                                                        ]}
                                                    />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>

                        <TablePagination
                            component="div"
                            count={pagination.total}
                            page={pagination.page - 1}
                            rowsPerPage={pagination.perPage}
                            onPageChange={(event, newPage) => {
                                handlePageChange(newPage)
                            }}
                            rowsPerPageOptions={[]}
                            sx={{ mt: 3 }}
                        />
                    </>
                )}
            </Box>
            <GalleryModal
                row={row}
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
            />
            <UploadModal
                open={isUploadModalOpen}
                onClose={handleCloseUploadModal}
            />
        </Box >
    );
}
